import React from "react";

/**
 * Iter76f — Production safety net.
 *
 * Without this, any uncaught render error in a child component (e.g.
 * an unexpected null in profile, a thrown selector, a broken animation)
 * would blank the entire Capacitor WebView — exactly the "white screen"
 * other testers were hitting after my Iter76d / Iter76e auth changes.
 *
 * We catch the error, dump it to console (so we still see it during
 * dev), and render a minimal recovery UI that:
 *   - Reassures the user it's a temporary glitch
 *   - Offers a "Reload app" button (window.location.reload)
 *   - Optionally shows the error message if it might help them tell us
 *     what they were doing (truncated to keep the UI readable)
 *
 * Wraps the whole app at the root in `src/App.js`.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: String(error?.message || error || "Unknown error"),
    };
  }

  componentDidCatch(error, info) {
    // Always log so we can grep production WebView logs.
    try {
      console.error("[ErrorBoundary] caught:", error, info?.componentStack);
    } catch (_) { /* noop */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex items-center justify-center bg-[#F7F5F0] p-6"
      >
        <div className="max-w-sm w-full rounded-3xl bg-white shadow-md p-6 text-center">
          <p className="text-5xl">🛠️</p>
          <h1 className="mt-3 text-xl font-medium text-[#2C332A]">Something glitched</h1>
          <p className="mt-2 text-sm text-[#2C332A]/70">
            Fitjong hit an unexpected error. Tap reload to recover — your
            progress is safe.
          </p>
          {this.state.message && (
            <p className="mt-3 text-xs text-[#2C332A]/40 break-words">
              {this.state.message.slice(0, 160)}
            </p>
          )}
          <button
            data-testid="error-boundary-reload-btn"
            onClick={this.handleReload}
            className="mt-5 w-full h-11 rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white font-medium"
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}
